import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, UserPlus, Link2, Unlink, Loader2 } from 'lucide-react';

export default function AudiologistLinksTab() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, u] = await Promise.all([api.get('/users/fundle-assignees', { params: { q } }), api.get('/users')]);
      setRows(a.data.data || []); setUsers(u.data || []);
    } catch { toast.error('Failed to load Fundle audiologists'); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const unlink = async (r) => {
    const u = users.find((x) => x.id === r.linked_user.user_id);
    if (!u || !window.confirm(`Unlink ${r.name} from ${u.email}? Their agent will stop seeing these patients.`)) return;
    try {
      await api.patch(`/users/${u.id}`, { fundle_names: (u.fundle_names || []).filter((n) => n.toLowerCase() !== r.name.toLowerCase()) });
      toast.success(`Unlinked ${r.name}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.toString() || 'Unlink failed'); }
  };

  return (
    <div className="space-y-3" data-testid="audiologist-links-tab">
      <div className="flex items-center gap-3">
        <div>
          <div className="font-heading text-lg font-bold text-stone-900">Audiologist Agents</div>
          <div className="text-xs text-stone-500">Pick an audiologist from Fundle and give them a login. Their AI agent then sees only the patients assigned to that name.</div>
        </div>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Fundle name…" className="h-8 pl-8 w-56" data-testid="fundle-assignee-search" />
        </div>
        <Button size="sm" className="h-8 bg-stone-900 hover:bg-stone-800 text-white" onClick={() => setBulkOpen(true)} data-testid="fundle-bulk-create">
          <UserPlus className="w-3.5 h-3.5 mr-1" /> Create logins for top audiologists
        </Button>
      </div>
      <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
            <tr><th className="text-left px-3 py-2">Fundle name</th><th className="text-right px-3 py-2">Patients</th><th className="text-left px-3 py-2">Clinics</th><th className="text-left px-3 py-2">Login</th><th /></tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && <tr><td colSpan={5} className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>}
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-stone-100" data-testid={`fundle-row-${r.name.replace(/\W+/g, '-')}`}>
                <td className="px-3 py-2 font-medium text-stone-900">{r.name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.patients.toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-stone-500 truncate max-w-[220px]">{r.clinics.join(', ')}</td>
                <td className="px-3 py-2 text-xs">{r.linked_user ? <span className="text-emerald-700 font-medium">{r.linked_user.email}</span> : <span className="text-stone-400">Not linked</span>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.linked_user ? (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-700" onClick={() => unlink(r)} data-testid={`fundle-unlink-${r.name.replace(/\W+/g, '-')}`}><Unlink className="w-3.5 h-3.5 mr-1" />Unlink</Button>
                  ) : (
                    <>
                      <Button size="sm" className="h-7 text-xs bg-stone-900 hover:bg-stone-800 text-white mr-1" onClick={() => setDlg({ mode: 'create', row: r })} data-testid={`fundle-create-${r.name.replace(/\W+/g, '-')}`}><UserPlus className="w-3.5 h-3.5 mr-1" />Create login</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDlg({ mode: 'link', row: r })} data-testid={`fundle-link-${r.name.replace(/\W+/g, '-')}`}><Link2 className="w-3.5 h-3.5 mr-1" />Link existing</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dlg && <LinkDialog dlg={dlg} users={users} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      {bulkOpen && <BulkDialog onClose={() => setBulkOpen(false)} onDone={load} />}
    </div>
  );
}

function BulkDialog({ onClose, onDone }) {
  const [top, setTop] = useState(12);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (pw.length < 8) return toast.error('Temporary password must be 8+ characters');
    setBusy(true);
    try {
      const { data } = await api.post('/users/fundle-assignees/bulk-create', { top: Number(top), temp_password: pw });
      setResult(data.data); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.toString() || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="fundle-bulk-dialog">
        <DialogHeader><DialogTitle>Create logins for top audiologists</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-2">
            <div className="text-xs text-stone-500">Uses each audiologist's email from the Staff list and links every Fundle spelling of their name. Existing logins are linked, never duplicated. Everyone must change the password on first login.</div>
            <div><Label className="text-xs">How many (by patient count)</Label><Input type="number" min={1} max={50} value={top} onChange={(e) => setTop(e.target.value)} data-testid="fundle-bulk-top" /></div>
            <div><Label className="text-xs">Temporary password</Label><Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="fundle-bulk-password" /></div>
          </div>
        ) : (
          <ul className="text-xs divide-y divide-stone-100 max-h-72 overflow-auto" data-testid="fundle-bulk-result">
            {result.map((r) => (
              <li key={r.name} className="py-1.5 flex gap-2"><b className="w-28 truncate">{r.name}</b><span className="flex-1 truncate text-stone-600">{r.email || r.reason}</span><span className={r.status === 'created' ? 'text-emerald-700' : 'text-stone-500'}>{r.status.replace('_', ' ')}</span></li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && <Button onClick={run} disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="fundle-bulk-run">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Create logins</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkDialog({ dlg, users, onClose, onDone }) {
  const [form, setForm] = useState({ full_name: dlg.row.name, email: '', password: '', user_id: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (dlg.mode === 'create') {
        if (!form.email || form.password.length < 8) { toast.error('Email and a password of 8+ characters are required'); setSaving(false); return; }
        await api.post('/users', { email: form.email, full_name: form.full_name, password: form.password, role: 'audiologist', fundle_names: [dlg.row.name], must_change_password: true });
      } else {
        const u = users.find((x) => x.id === form.user_id);
        if (!u) { toast.error('Pick a user'); setSaving(false); return; }
        await api.patch(`/users/${u.id}`, { fundle_names: Array.from(new Set([...(u.fundle_names || []), dlg.row.name])) });
      }
      toast.success(`${dlg.row.name} linked`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.toString() || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="fundle-link-dialog">
        <DialogHeader><DialogTitle>{dlg.mode === 'create' ? 'Create audiologist login' : 'Link to existing login'} · {dlg.row.name}</DialogTitle></DialogHeader>
        {dlg.mode === 'create' ? (
          <div className="space-y-2">
            <div><Label className="text-xs">Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="fundle-new-name" /></div>
            <div><Label className="text-xs">Email (login)</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fundle-new-email" /></div>
            <div><Label className="text-xs">Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="fundle-new-password" />
              <div className="text-[10px] text-stone-500 mt-1">They'll be asked to change it on first login.</div></div>
          </div>
        ) : (
          <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
            <SelectTrigger data-testid="fundle-link-user"><SelectValue placeholder="Pick a user" /></SelectTrigger>
            <SelectContent>{users.filter((u) => u.role === 'audiologist').map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.email}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="fundle-link-save">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
